import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import { actionCreators } from "../../state/index";
import { format } from "date-fns";
import { Pagination } from "antd";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LawGrievanceAPI from "../../utils/api/LawGrievanceAPI";
import { TableSkeleton } from "../skeletons";
import "./law.css";

const AVATAR_COLORS = [
  "#6366f1", "#0ea5e9", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6",
];

const avatarColor = (str = "") => {
  let sum = 0;
  for (let i = 0; i < str.length; i++) sum += str.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const initials = (name = "") => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "?";
  return ((parts[0][0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

const capitalizeWords = (name) => {
  if (!name) return "";
  return name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const LawGrievanceTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { top_loader } = bindActionCreators(actionCreators, dispatch);

  const [recperpage, SetRecPerPage] = useState(25);
  const [activepage, SetActivePage] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const sno = recperpage * (activepage - 1);

  const getAll = (params) => {
    setLoading(true);
    top_loader(true, 45);
    LawGrievanceAPI.getAll(params)
      .then((result) => {
        if (result.data.status) {
          setData(result.data.data);
          setTotalData(result.data.total);
        } else {
          setData([]);
          setTotalData(0);
        }
        setLoading(false);
        top_loader(true, 100);
        top_loader(false, 0);
      })
      .catch(() => {
        setData([]);
        setLoading(false);
        top_loader(true, 100);
        top_loader(false, 0);
      });
  };

  useEffect(() => {
    getAll({ page: activepage, limit: recperpage });
    // eslint-disable-next-line
  }, [activepage, recperpage]);

  const handleRefresh = () => {
    SetActivePage(1);
    getAll({ page: 1, limit: recperpage });
  };

  return (
    <Container fluid className="law-page custom-sidebar">
      <div className="law-shell">
        {/* Header */}
        <div className="law-header">
          <div className="law-header__left">
            <span className="law-header__icon">
              <GavelIcon style={{ color: "#fff" }} />
            </span>
            <div>
              <h1 className="law-header__title">Law Grievance Form</h1>
              <p className="law-header__subtitle">
                Grievances submitted by law students
              </p>
            </div>
          </div>
          <div className="law-header__actions">
            <span className="law-count-pill">{totalData} Total</span>
            <button
              className="law-icon-btn"
              onClick={handleRefresh}
              title="Refresh"
            >
              <RefreshOutlinedIcon />
            </button>
          </div>
        </div>

        {/* Table card */}
        <div className="law-card">
          {loading ? (
            <div style={{ padding: "16px" }}>
              <TableSkeleton rows={10} columns={7} hasActions={false} />
            </div>
          ) : (
            <div className="law-table-wrap">
              <table className="law-table">
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>#</th>
                    <th>Student</th>
                    <th>Email</th>
                    <th>Program</th>
                    <th>Problem Type</th>
                    <th>Submitted</th>
                    <th style={{ textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length > 0 &&
                    data.map((item, index) => (
                      <tr
                        key={item._id || index}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(`/dashboard/law-grievance/${item._id}`)
                        }
                      >
                        <td className="law-muted">{sno + index + 1}</td>
                        <td>
                          <div className="law-name-cell">
                            <span
                              className="law-avatar"
                              style={{ background: avatarColor(item.student_name) }}
                            >
                              {initials(item.student_name)}
                            </span>
                            <span className="law-name-primary">
                              {capitalizeWords(item.student_name)}
                            </span>
                          </div>
                        </td>
                        <td>{item.email}</td>
                        <td>
                          <span className="law-chip law-chip--program">
                            {item.program}
                          </span>
                        </td>
                        <td>
                          <span className="law-chip law-chip--type">
                            {item.problem_type}
                          </span>
                        </td>
                        <td className="law-muted">
                          {item.created_at
                            ? format(new Date(item.created_at), "dd MMM yyyy")
                            : "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <span className="law-link">
                            View <ChevronRightIcon style={{ fontSize: "16px" }} />
                          </span>
                        </td>
                      </tr>
                    ))}
                  {data.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <div className="law-empty">
                          <InboxOutlinedIcon style={{ fontSize: "44px", color: "#cbd5e1" }} />
                          <h3>No grievances yet</h3>
                          <p>Submitted grievances will appear here.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="law-footer">
            <div className="law-rows-per-page">
              <span>Rows per page</span>
              <select
                onChange={(event) => {
                  SetRecPerPage(Number(event.target.value));
                  SetActivePage(1);
                }}
                value={recperpage}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="75">75</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
            <Pagination
              total={totalData}
              showSizeChanger={false}
              current={activepage}
              size="small"
              pageSize={recperpage}
              onChange={(page) => SetActivePage(page)}
            />
          </div>
        </div>
      </div>
    </Container>
  );
};

export default LawGrievanceTable;
